import type { Notification } from '../types';
import type { Translations } from '../i18n';
import { roleLabelOf } from './fillTemplate';

// Renders a notification row in the user's language. The row carries a
// message key (`template`, or the `type`) and `params`; the template comes
// from `t.notifications.messages`. A row with no template, or a key the
// dictionary does not know (old rows, a free-text ping), falls back to the
// stored English `title` / `body`.
//
// Template syntax: `{name}` is a parameter. A `[[ ... ]]` segment is dropped
// when any parameter inside it resolves to an empty string, so an optional
// part ("Opponent: X.") disappears cleanly. `{role}` maps through `t.roles`.
// `{seats}` is the seat list of an official (#557): a JSON list of
// [bout number, ring, seat, day]. Each entry shows as "#12 · Ring A · Judge",
// with the seat through `t.roles`, and the `more` parameter adds "+N".

export interface RenderedNotification { title: string; body: string }

/** #557 — "#12 · Ring A · Judge; #14 · 2026-10-11 · Ring B · Jury; +2". */
function seatLines(raw: string, more: unknown, t: Translations): string {
  let list: unknown;
  try { list = JSON.parse(raw); } catch { return raw; }
  if (!Array.isArray(list)) return raw;
  const lines = list.map(entry => {
    const [n, ring, seat, day] = Array.isArray(entry) ? entry : [];
    return [`#${n ?? '?'}`, day ? String(day) : '', String(ring ?? '—'), roleLabelOf(t, String(seat))].filter(Boolean).join(' · ');
  });
  const rest = Number(more);
  return lines.join('; ') + (rest > 0 ? `; +${rest}` : '');
}

function substitute(template: string, params: Record<string, string | number>, t: Translations): string {
  const value = (key: string): string => {
    const raw = params[key];
    if (raw === undefined || raw === null || raw === '') return '';
    if (key === 'role') return roleLabelOf(t, String(raw));
    if (key === 'seats') return seatLines(String(raw), params.more, t);
    return String(raw);
  };
  const optional = template.replace(/\[\[(.*?)\]\]/g, (_m, inner: string) => {
    const keys = [...inner.matchAll(/\{(\w+)\}/g)].map(m => m[1]);
    return keys.some(k => value(k) === '') ? '' : inner;
  });
  // A text that starts with an optional part can start with a space.
  return optional.replace(/\{(\w+)\}/g, (_m, key: string) => value(key)).trim();
}

export function renderNotification(n: Notification, t: Translations): RenderedNotification {
  const key = n.template ?? n.type;
  const tpl = n.params ? t.notifications.messages[key] : undefined;
  if (!tpl) return { title: n.title, body: n.body ?? '' };
  return {
    title: substitute(tpl.title, n.params!, t),
    body: substitute(tpl.body, n.params!, t),
  };
}
